'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { motion, HTMLMotionProps } from 'framer-motion';

interface IconContainerProps extends HTMLMotionProps<'div'> {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'error' | 'warning' | 'muted';
  animate?: boolean;
  borderless?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const IconContainer = React.forwardRef<HTMLDivElement, IconContainerProps>(
  (
    { className, children, size = 'md', variant = 'default', animate = false, borderless = false, icon, ...props },
    ref
  ) => {
    const sizeClasses = {
      xs: 'h-5 w-5',
      sm: 'h-8 w-8',
      md: 'h-12 w-12',
      lg: 'h-16 w-16'
    };

    const variantClasses = borderless
      ? {
          default: 'bg-accent text-white',
          success: 'bg-success text-white',
          error: 'bg-destructive text-white',
          warning: 'bg-warning text-white',
          muted: 'bg-muted-foreground/10 text-muted-foreground'
        }
      : {
          default: 'border-accent bg-accent/20 text-accent',
          success: 'border-success bg-success/20 text-success',
          error: 'border-destructive bg-destructive/20 text-destructive',
          warning: 'border-warning bg-warning/20 text-warning',
          muted: 'border-muted-foreground/10 bg-muted-foreground/10 text-muted-foreground'
        };

    return (
      <motion.div
        ref={ref}
        className={cn(
          'rounded-full flex items-center justify-center  flex-shrink-0',
          sizeClasses[size],
          variantClasses[variant],
          !borderless && 'border',
          className
        )}
        {...(animate
          ? {
              initial: { scale: 0, opacity: 0 },
              animate: { scale: 1, opacity: 1 },
              transition: {
                type: 'spring',
                stiffness: 300,
                damping: 20,
                mass: 1
              }
            }
          : {})}
        {...props}
      >
        {icon}
        {children}
      </motion.div>
    );
  }
);

IconContainer.displayName = 'IconContainer';

export { IconContainer };
